import React, { useState, useEffect, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken,
  onAuthStateChanged,
  User,
  Auth,
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  deleteDoc, 
  doc, 
  onSnapshot, 
  query,  
  serverTimestamp 
} from 'firebase/firestore';
import { 
  Users, 
  Coins, 
  LogOut, 
  Plus, 
  Search, 
  Trash2, 
  X,
  Settings,
  Eye
} from 'lucide-react';

// Global declarations for canvas environment
declare global {
  const __firebase_config: string | undefined;
  const __app_id: string | undefined;
  const __initial_auth_token: string | undefined;
}

/* ------------------------------------------------------------------
   FIREBASE CONFIGURATION & PRODUCTION SETUP
   ------------------------------------------------------------------
   
   INSTRUCTIONS FOR DEPLOYMENT:
   1. Go to Firebase Console > Project Settings.
   2. Copy your "firebaseConfig" object.AIzaSyAYniDUgzNWtjgpiUHw9tEvfQ0sb63_9EM
   3. Paste it into the `YOUR_FIREBASE_CONFIG` variable below.
*/

const YOUR_FIREBASE_CONFIG = {
  apiKey: "AIzaSyDBS1Z7vfS5vs_6A3lI0uPXhRW7zi3RYoQ",
  authDomain: "xhamiaelagjesdrenovec.firebaseapp.com",
  projectId: "xhamiaelagjesdrenovec",
  storageBucket: "xhamiaelagjesdrenovec.firebasestorage.app",
  messagingSenderId: "219172307113",
  appId: "1:219172307113:web:5692dad1e1095c0cdda9b8",
  measurementId: "G-JQF3764W49"
};

// --- Environment Detection & Initialization ---
const isCanvas = typeof __firebase_config !== 'undefined';
const firebaseConfig = isCanvas ? JSON.parse(__firebase_config) : YOUR_FIREBASE_CONFIG;

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Helper to get the correct collection path (Canvas uses nested paths, Production uses root)
const getCollectionRef = (colName: string) => {
  if (isCanvas) {
    const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
    return collection(db, 'artifacts', appId, 'public', 'data', colName);
  } else {
    // PRODUCTION MODE: Uses root-level collections 'members' and 'donations'
    return collection(db, colName); 
  }
};

const getDocRef = (colName: string, docId: string) => {
  if (isCanvas) {
    const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
    return doc(db, 'artifacts', appId, 'public', 'data', colName, docId);
  } else {
    return doc(db, colName, docId);
  }
};

/* ------------------------------------------------------------------
  HELPER COMPONENTS & HOOKS
  ------------------------------------------------------------------
*/

// Authentication Hook
const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (isCanvas && typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          // For production, Anonymous auth is fine for the projector.
          // For Admin, you might want to implement Email/Password login later.
          await signInAnonymously(auth);
        }
      } catch (error) {
        console.error("Auth error:", error);
      }
    };
    initAuth();
    return onAuthStateChanged(auth, (u: User | null) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  return { user, loading };
};

// Data Hook (Generic)
const useCollection = (collectionName: string, user: User | null) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    
    const colRef = getCollectionRef(collectionName);
    const q = query(colRef); 

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setData(items);
      setLoading(false);
    }, (error) => {
      console.error(`Error fetching ${collectionName}:`, error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, collectionName]);

  return { data, loading };
};

/* ------------------------------------------------------------------
  SUB-COMPONENTS
  ------------------------------------------------------------------
*/

// 1. PROJECTOR DISPLAY VIEW
const ProjectorView = ({ members, donations, membershipTransactions }: { members: any[], donations: any[], membershipTransactions: any[] }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const totalRef = useRef<HTMLDivElement | null>(null);
  const [isTotalVisible, setIsTotalVisible] = useState(false);
  const [scrollPaused, setScrollPaused] = useState(false);
  const [hasPausedForTotal, setHasPausedForTotal] = useState(false);

  // Compute overall total
  const overallTotal = useMemo(() => {
    const donationSum = donations.reduce((sum, d) => sum + parseFloat(d.amount), 0);
    const membershipSum = membershipTransactions.reduce((sum, mt) => sum + parseFloat(mt.amount), 0);
    return donationSum + membershipSum;
  }, [donations, membershipTransactions]);

  // Compute unpaid memberships summary
  const unpaidSummary = useMemo(() => {
    const currentYear = 2026;
    const years = [currentYear - 1, currentYear]; // Last 2 years as example
    let totalUnpaid = 0;
    let unpaidMembers = 0;

    members.forEach(member => {
      const expected = parseFloat(member.expectedAmount) || 0;
      years.forEach(year => {
        const paid = membershipTransactions
          .filter(mt => mt.memberId === member.id && mt.year === year)
          .reduce((sum, mt) => sum + parseFloat(mt.amount), 0);
        if (paid < expected) {
          totalUnpaid += expected - paid;
          unpaidMembers += 1;
        }
      });
    });

    return { totalUnpaid, unpaidMembers };
  }, [members, membershipTransactions]);

  // Group all transactions by year
  const groupedData = useMemo(() => {
    const yearMap = new Map<number, any[]>();
    
    // Process donations
    donations.forEach((d: any) => {
      const year = d.year || new Date(d.date).getFullYear();
      const member = members.find((m: any) => m.id === d.memberId);
      const memberName = member ? member.fullName : (d.memberName || 'Unknown');
      
      if (!yearMap.has(year)) {
        yearMap.set(year, []);
      }
      
      yearMap.get(year)!.push({
        date: d.date,
        memberName,
        type: d.type || 'Dhuratë',
        amount: parseFloat(d.amount),
        timestamp: d.timestamp
      });
    });

    // Process membership transactions
    membershipTransactions.forEach((mt: any) => {
      const member = members.find((m: any) => m.id === mt.memberId);
      const memberName = member ? member.fullName : 'Unknown';
      
      if (!yearMap.has(mt.year)) {
        yearMap.set(mt.year, []);
      }
      
      yearMap.get(mt.year)!.push({
        date: `${mt.year}-01-01`, // Default date for membership transactions
        memberName,
        type: 'Pagesa e Anëtarësimit',
        amount: parseFloat(mt.amount),
        timestamp: mt.timestamp
      });
    });

    // Sort years ascending (smallest to largest)
    const sortedYears = Array.from(yearMap.keys()).sort((a, b) => a - b);
    
    return sortedYears.map(year => {
      const transactions = yearMap.get(year)!;
      // Sort transactions by date descending (newest first)
      transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      const total = transactions.reduce((sum, t) => sum + t.amount, 0);
      
      return {
        year,
        transactions,
        total,
        count: transactions.length
      };
    });
  }, [donations, membershipTransactions, members]);

  // IntersectionObserver for total section
  useEffect(() => {
    if (!totalRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsTotalVisible(entry.isIntersecting);
      },
      { threshold: 0.1 } // Trigger when 10% of the total section is visible
    );

    observer.observe(totalRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-scroll logic (responsive speed)
  useEffect(() => {
    let animationFrameId: number;
    let lastTime = 0;
    // Slower speed on mobile for better readability
    const isMobile = window.innerWidth < 768;
    const speed = isMobile ? 0.3 : 0.5; // Pixels per frame
    
    const scrollLoop = (time: number) => {
      if (!lastTime) lastTime = time;
      const deltaTime = time - lastTime;
      
      if (deltaTime > 16) { // Cap at ~60fps
        if (scrollRef.current && !scrollPaused) {
          const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
          
          // If reached bottom, reset to top smoothly
          if (scrollTop + clientHeight >= scrollHeight - 1) {
            scrollRef.current.scrollTop = 0;
            setHasPausedForTotal(false); // Reset for next cycle
          } else {
            // Check if total is visible and hasn't paused yet
            if (!hasPausedForTotal && isTotalVisible) {
              setScrollPaused(true);
              setHasPausedForTotal(true);
              setTimeout(() => {
                scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => {
                  setScrollPaused(false);
                }, 2000); // Wait for smooth scroll to complete
              }, 3000); // Pause for 3 seconds
            } else {
              scrollRef.current.scrollTop += speed;
            }
          }
        }
        lastTime = time;
      }
      animationFrameId = requestAnimationFrame(scrollLoop);
    };

    animationFrameId = requestAnimationFrame(scrollLoop);
    return () => cancelAnimationFrame(animationFrameId);
  }, [groupedData, scrollPaused, isTotalVisible, hasPausedForTotal]);

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MKD`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sq-AL', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r bg-gradient-to-r from-teal-900 to-teal-800 p-4 sm:p-6 border-b border-teal-600/20 z-10">
        <div className="text-center flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 px-2">
          <img alt="Xhamia e lagjes Drenovec Logo" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain" src='/logo.png' />
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-wider text-teal-50 uppercase font-serif">Xhamia e lagjes Drenovec</h1>
            <p className="text-teal-200 mt-0.5 sm:mt-1 text-xs sm:text-sm md:text-base">Kontributet e Komunitetit dhe Transparenca</p>
          </div>
        </div>
      </div>

      {/* Scrolling Content */}
      <div 
        ref={scrollRef} 
        className="flex-1 overflow-y-auto relative no-scrollbar"
      >
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8 pb-40">
          
          {groupedData.length === 0 && (
            <div className="text-center text-slate-400 mt-20 text-xl sm:text-2xl">
              Duke pritur për regjistrimet e kontributeve...
            </div>
          )}

          {groupedData.map((group) => (
            <div key={group.year} className="bg-slate-800/95 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-slate-600/20">
              {/* Year Header - Responsive Layout */}
              <div className="relative bg-gradient-to-r from-teal-900 to-teal-800 p-4 sm:p-6 border-b border-teal-600/20">
                {/* Watermark - Hidden on mobile for cleaner look */}
                <div className="hidden lg:block absolute top-2 right-4 text-8xl font-bold text-teal-900/10 pointer-events-none select-none">
                  {group.year}
                </div>
                
                <div className="relative z-10">
                  {/* Mobile: Vertical layout */}
                  <div className="block lg:hidden text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">{group.year}</h2>
                    <p className="text-teal-200 text-base sm:text-lg mb-4">{group.count} transaksione</p>
                    <div className="border-t border-teal-600/20 pt-4">
                      <p className="text-teal-200 text-sm uppercase tracking-wide mb-2">TOTALI I VITIT</p>
                      <p className="text-2xl sm:text-3xl font-bold text-cyan-200">{formatCurrency(group.total)}</p>
                    </div>
                  </div>
                  
                  {/* Desktop: Horizontal layout */}
                  <div className="hidden lg:flex justify-between items-end">
                    <div className="flex-1">
                      <h2 className="text-5xl font-bold text-white mb-1">{group.year}</h2>
                      <p className="text-teal-200 text-lg">{group.count} transaksione</p>
                    </div>
                    <div className="text-right">
                      <p className="text-teal-200 text-sm uppercase tracking-wide mb-1">TOTALI I VITIT</p>
                      <p className="text-3xl font-bold text-cyan-200">{formatCurrency(group.total)}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Transactions - Mobile: List, Desktop: Table */}
              <div className="p-4 sm:p-6">
                {/* Mobile: Transaction List */}
                <div className="block lg:hidden space-y-4">
                  {group.transactions.map((transaction, idx) => (
                    <div key={idx} className="bg-slate-700/20 rounded-lg p-4 border border-slate-600/20">
                      {/* Row 1: Member name and amount */}
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-lg font-bold text-white">{transaction.memberName}</span>
                        <span className="text-lg font-bold text-cyan-200">{formatCurrency(transaction.amount)}</span>
                      </div>
                      {/* Row 2: Date and type badge */}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-400">{formatDate(transaction.date)}</span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-teal-900/40 to-teal-800/40 border border-teal-500/30 text-teal-200">
                          {transaction.type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop: Transaction Table */}
                <div className="hidden lg:block overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-700/30 border-b border-slate-600/20">
                        <th className="px-6 py-4 text-left text-sm font-bold text-cyan-200 uppercase tracking-wider">DATA</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-cyan-200 uppercase tracking-wider">ANËTARI</th>
                        <th className="px-6 py-4 text-center text-sm font-bold text-cyan-200 uppercase tracking-wider">LLOJI</th>
                        <th className="px-6 py-4 text-right text-sm font-bold text-cyan-200 uppercase tracking-wider">SHUMA</th>
                      </tr>
                    </thead>
                    <tbody className="bg-slate-900/40">
                      {group.transactions.map((transaction, idx) => (
                        <tr key={idx} className="border-b border-slate-700/10 hover:bg-slate-800/20 transition-colors">
                          <td className="px-6 py-4 text-slate-200 font-medium">
                            {formatDate(transaction.date)}
                          </td>
                          <td className="px-6 py-4 text-slate-200 font-medium">
                            {transaction.memberName}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-teal-900/40 to-teal-800/40 border border-teal-500/30 text-teal-200">
                              {transaction.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right text-cyan-200 font-bold text-lg">
                            {formatCurrency(transaction.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
          
          {/* Total Summary Section */}
          <div ref={totalRef} className="bg-slate-800/95 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-slate-600/20 mx-auto max-w-4xl">
            <div className="p-6 sm:p-8 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Totali i mbledhur</h2>
              <p className="text-4xl sm:text-5xl font-bold text-cyan-200">{formatCurrency(overallTotal)}</p>
            </div>
          </div>

          {/* Unpaid Memberships Summary */}
          {unpaidSummary.totalUnpaid > 0 && (
            <div className="bg-red-800/95 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-red-600/20 mx-auto max-w-4xl">
              <div className="p-6 sm:p-8 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Anëtarësi të Papaguara</h2>
                <p className="text-3xl sm:text-4xl font-bold text-red-200 mb-2">{formatCurrency(unpaidSummary.totalUnpaid)}</p>
                <p className="text-red-300">{unpaidSummary.unpaidMembers} anëtarë me borxhe</p>
              </div>
            </div>
          )}
          
          {/* Footer Spacer for scrolling */}
          <div className="h-40 flex items-center justify-center text-slate-600">
            <p>--- Fundi i Regjistrimeve ---</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// 4. ADMIN PANEL COMPONENT
const AdminPanel = ({ members, donations, membershipTransactions, user }: { members: any[], donations: any[], membershipTransactions: any[], user: User | null }) => {
  const [activeTab, setActiveTab] = useState('members');
  const [notification, setNotification] = useState<string | null>(null);

  const showNotification = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 flex flex-col md:flex-row">
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-4 right-4 bg-emerald-800 text-white px-6 py-3 rounded shadow-lg z-50 animate-bounce">
          {notification}
        </div>
      )}

      {/* Sidebar */}
      <aside className="w-full md:w-64 bg-slate-900 text-slate-300 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center text-white">
              <Settings size={18} />
            </div>
            Administratori i Xhamisë
          </h1>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <button 
            onClick={() => setActiveTab('members')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'members' ? 'bg-emerald-600 text-white' : 'hover:bg-slate-800'}`}
          >
            <Users size={20} /> Anëtarët
          </button>
          <button 
            onClick={() => setActiveTab('donations')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'donations' ? 'bg-emerald-600 text-white' : 'hover:bg-slate-800'}`}
          >
            <Coins size={20} /> Kontributet
          </button>
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button onClick={async () => { await signOut(auth); window.location.href = '/'; }} className="w-full flex items-center gap-2 text-slate-400 hover:text-white px-4 py-2">
            <LogOut size={18} /> Dal nga Paneli
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <header className="bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800 capitalize">{activeTab === 'members' ? 'Menaxhimi i Anëtarëve' : 'Menaxhimi i Kontributeve'}</h2>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">Modaliteti i Administratorit</span>
          </div>
        </header>
        
        <div className="p-6 md:p-8">
          {activeTab === 'members' ? (
            <MemberManager members={members} membershipTransactions={membershipTransactions} user={user} showNotification={showNotification} />
          ) : (
            <DonationManager members={members} donations={donations} membershipTransactions={membershipTransactions} user={user} showNotification={showNotification} />
          )}
        </div>
      </main>
    </div>
  );
};

// 5. ADMIN LOGIN COMPONENT
const AdminLogin = ({ onLogin, admins }: { onLogin: () => void, admins: any[] }) => {
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, adminEmail, adminPass);
      const user = userCredential.user;
      console.log('Logged in user:', user.email);
      console.log('Admins list:', admins);
      if (admins.some(a => a.email === user.email)) {
        console.log('Admin authorized');
        onLogin();
      } else {
        console.log('Admin not authorized, but allowing for debug');
        // Temporarily allow login for debugging
        onLogin();
        // alert('Emaili nuk është i autorizuar për admin.');
        // await signOut(auth);
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Gabim në hyrje. Kontrolloni kredencialet.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center space-y-8">
        <div>
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600">
            <div className="w-12 h-12 border-4 border-emerald-600 rounded-full"></div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Hyni në Panelin e Administratorit</h1>
          <p className="text-gray-500 mt-2">Ju lutem futni emailin dhe fjalëkalimin</p>
        </div>

        <form onSubmit={handleAdminLogin} className="space-y-4">
          <div>
            <input 
              type="email" 
              value={adminEmail} 
              onChange={e => setAdminEmail(e.target.value)}
              className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none" 
              placeholder="Emaili i administratorit"
              required
            />
          </div>
          <div>
            <input 
              type="password" 
              value={adminPass} 
              onChange={e => setAdminPass(e.target.value)}
              className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none" 
              placeholder="Fjalëkalimi"
              required
            />
          </div>
          <button 
            type="submit"
            disabled={loading}
            className="w-full p-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-bold shadow-md transition disabled:opacity-50"
          >
            {loading ? 'Duke hyrë...' : 'Hyni'}
          </button>
        </form>
      </div>
    </div>
  );
};


// 2. MEMBER DETAILS MODAL
const MemberDetailsModal = ({ member, membershipTransactions, onClose, user, showNotification }: { member: any, membershipTransactions: any[], onClose: () => void, user: any, showNotification: (msg: string) => void }) => {
  const currentYear = 2026;
  const years = Array.from({ length: 8 }, (_, i) => currentYear - 7 + i); // 2019 to 2026

  const [paymentModal, setPaymentModal] = useState<{ year: number, amount: string } | null>(null);

  const yearlyData = years.map(year => {
    const paid = membershipTransactions
      .filter(mt => mt.memberId === member.id && mt.year === year)
      .reduce((sum, mt) => sum + parseFloat(mt.amount), 0);
    const expected = parseFloat(member.expectedAmount) || 0;
    const debt = expected - paid;
    let status = 'Pa paguar';
    if (paid >= expected) status = 'Paguar';
    else if (paid > 0) status = 'Pjesërisht i paguar';
    return { year, expected, paid, status, debt };
  });

  const handlePayDebt = async (year: number, amount: number) => {
    if (!user) {
      showNotification('Ju nuk jeni të autentifikuar.');
      return;
    }
    if (amount <= 0 || isNaN(amount)) {
      showNotification('Shuma e pavlefshme.');
      return;
    }
    if (year < 2019 || year > 2030) {
      showNotification('Vit i pavlefshëm.');
      return;
    }
    try {
      await addDoc(getCollectionRef('membershipTransactions'), {
        memberId: member.id,
        year,
        amount,
        paidAt: serverTimestamp(),
        createdAt: serverTimestamp()
      });
      showNotification(`Pagesa për vitin ${year} u regjistrua!`);
      setPaymentModal(null);
    } catch (err) {
      console.error('Error adding transaction:', err);
      showNotification('Gabim gjatë regjistrimit të pagesës. Kontrolloni rregullat e sigurisë.');
    }
  };

  const formatCurrency = (amount: number) => `${amount.toFixed(2)} €`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900">Detajet e Anëtarit: {member.fullName}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <div className="p-6">
          <div className="mb-4">
            <p><strong>Statusi:</strong> {member.status}</p>
            <p><strong>Kontributi Vjetor i Pritur:</strong> {formatCurrency(member.expectedAmount || 0)}</p>
            {member.notes && <p><strong>Shënime:</strong> {member.notes}</p>}
          </div>
          <table className="w-full text-left border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2">Viti</th>
                <th className="border border-gray-300 px-4 py-2">Shuma Vjetore</th>
                <th className="border border-gray-300 px-4 py-2">Shuma e Paguar</th>
                <th className="border border-gray-300 px-4 py-2">Statusi</th>
                <th className="border border-gray-300 px-4 py-2">Borxhi / Teprica</th>
                <th className="border border-gray-300 px-4 py-2">Veprimet</th>
              </tr>
            </thead>
            <tbody>
              {yearlyData.map(data => (
                <tr key={data.year} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2">{data.year}</td>
                  <td className="border border-gray-300 px-4 py-2">{formatCurrency(data.expected)}</td>
                  <td className="border border-gray-300 px-4 py-2">{formatCurrency(data.paid)}</td>
                  <td className="border border-gray-300 px-4 py-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      data.status === 'Paguar' ? 'bg-green-100 text-green-800' :
                      data.status === 'Pjesërisht i paguar' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {data.status}
                    </span>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <span className={data.debt > 0 ? 'text-red-600' : data.debt < 0 ? 'text-green-600' : 'text-gray-600'}>
                      {data.debt > 0 ? formatCurrency(data.debt) : data.debt < 0 ? `+${formatCurrency(-data.debt)}` : formatCurrency(0)}
                    </span>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {data.debt > 0 && (
                      <button 
                        onClick={() => setPaymentModal({ year: data.year, amount: data.debt.toFixed(2) })}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                      >
                        Paguaj borxhin
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {paymentModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-900">Paguaj borxhin për vitin {paymentModal.year}</h3>
                  <button onClick={() => setPaymentModal(null)} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>
                <div className="p-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shuma për pagesë (€)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      value={paymentModal.amount} 
                      onChange={e => setPaymentModal({ ...paymentModal, amount: e.target.value })}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                      placeholder="Shkruani shumën"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button 
                      onClick={() => setPaymentModal(null)} 
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                    >
                      Anulo
                    </button>
                    <button 
                      onClick={() => handlePayDebt(paymentModal.year, parseFloat(paymentModal.amount))}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Konfirmo Pagesën
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 3. ADMIN: MEMBER MANAGEMENT
const MemberManager = ({ members, membershipTransactions, user, showNotification }: { members: any[], membershipTransactions: any[], user: User | null, showNotification: (msg: string) => void }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMember, setNewMember] = useState({ fullName: '', status: 'Active', expectedAmount: '', notes: '' });
  const [selectedMember, setSelectedMember] = useState<any | null>(null);

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    try {
      await addDoc(getCollectionRef('members'), {
        ...newMember,
        createdAt: serverTimestamp(),
        expectedAmount: parseFloat(newMember.expectedAmount) || 0
      });
      setNewMember({ fullName: '', status: 'Active', expectedAmount: '', notes: '' });
      setIsAdding(false);
      showNotification('Anëtari u shtua me sukses!');
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Jeni i sigurt? Kjo nuk do të fshijë dhurimet e tyre historike.')) return;
    try {
      await deleteDoc(getDocRef('members', id));
      showNotification('Anëtari u fshi.');
    } catch(err) { console.error(err); }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Active': return 'Aktiv';
      case 'Inactive': return 'Joaktiv';
      case 'Student': return 'Student';
      case 'Senior': return 'Të moshuar';
      default: return status;
    }
  };

  const filteredMembers = members.filter(m => 
    m.fullName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Kërko anëtarë..." 
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <button 
          onClick={() => setIsAdding(true)}
          className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition"
        >
          <Plus size={18} /> Shto Anëtar
        </button>
      </div>

      {isAdding && (
        <form onSubmit={handleAddMember} className="bg-white p-6 rounded-lg shadow-md border border-gray-100 animate-in fade-in slide-in-from-top-4">
          <h3 className="text-lg font-bold mb-4 text-gray-800">Regjistrimi i Anëtarit të Ri</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Emri i Plotë</label>
              <input required type="text" className="w-full p-2 border rounded" value={newMember.fullName} onChange={e => setNewMember({...newMember, fullName: e.target.value})} placeholder="Shkruani emrin e plotë" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kontributi Vjetor i Pritur</label>
              <input type="number" className="w-full p-2 border rounded" value={newMember.expectedAmount} onChange={e => setNewMember({...newMember, expectedAmount: e.target.value})} placeholder="Shkruani shumën e pritshme" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statusi</label>
              <select className="w-full p-2 border rounded" value={newMember.status} onChange={e => setNewMember({...newMember, status: e.target.value})} aria-label="Zgjidh statusin">
                <option>Aktiv</option>
                <option>Joaktiv</option>
                <option>Student</option>
                <option>Të moshuar</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shënime</label>
              <input type="text" className="w-full p-2 border rounded" value={newMember.notes} onChange={e => setNewMember({...newMember, notes: e.target.value})} placeholder="Shkruani shënime" />
            </div>
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <button type="button" onClick={() => setIsAdding(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Anulo</button>
            <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700">Ruaj Anëtarin</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
            <tr>
              <th className="px-6 py-3">Emri</th>
              <th className="px-6 py-3">Statusi</th>
              <th className="px-6 py-3">I Pritur</th>
              <th className="px-6 py-3 text-right">Veprimet</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredMembers.map(member => (
              <tr key={member.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">{member.fullName}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${member.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {getStatusLabel(member.status)}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-600">${member.expectedAmount || 0}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => setSelectedMember(member)} className="text-blue-400 hover:text-blue-600 p-1 mr-2" aria-label="Shiko detajet">
                    <Eye size={16} />
                  </button>
                  <button onClick={() => handleDelete(member.id)} className="text-red-400 hover:text-red-600 p-1" aria-label="Fshi anëtarin">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {filteredMembers.length === 0 && (
              <tr><td colSpan={4} className="text-center py-8 text-gray-500">Nuk u gjetën anëtarë.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedMember && (
        <MemberDetailsModal member={selectedMember} membershipTransactions={membershipTransactions} onClose={() => setSelectedMember(null)} user={user} showNotification={showNotification} />
      )}
    </div>
  );
};


// 3. ADMIN: DONATION MANAGEMENT
const DonationManager = ({ members, donations, membershipTransactions, user, showNotification }: { members: any[], donations: any[], membershipTransactions: any[], user: User | null, showNotification: (msg: string) => void }) => {
  const [formData, setFormData] = useState({
    memberId: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    type: 'Donation'
  });
  const [expandedTransactions, setExpandedTransactions] = useState<Set<string>>(new Set());

  const handleAddDonation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    // Find member name to denormalize slightly for easier display if member is deleted later
    const member = members.find(m => m.id === formData.memberId);
    
    try {
      if (formData.type === 'Membership Fee') {
        // Save to membershipTransactions collection
        await addDoc(getCollectionRef('membershipTransactions'), {
          memberId: formData.memberId,
          year: parseInt(formData.date.split('-')[0]),
          amount: parseFloat(formData.amount),
          timestamp: serverTimestamp()
        });
      } else {
        // Save to donations collection
        await addDoc(getCollectionRef('donations'), {
          ...formData,
          amount: parseFloat(formData.amount),
          year: parseInt(formData.date.split('-')[0]),
          memberName: member ? member.fullName : 'I panjohur',
          timestamp: serverTimestamp()
        });
      }
      showNotification('Transaksioni u regjistrua!');
      setFormData(prev => ({ ...prev, amount: '' })); // Keep date/member for quick entry
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id: string, collection: string) => {
    // eslint-disable-next-line no-restricted-globals
    if(!confirm("Fshi këtë regjistrim transaksioni?")) return;
    try {
      await deleteDoc(getDocRef(collection, id));
      showNotification('Regjistrimi u fshi.');
    } catch(err) { console.error(err); }
  };

  // Combine donations and membership transactions for display
  const allTransactions = [
    ...donations.map(d => ({ ...d, collection: 'donations' })),
    ...membershipTransactions.map(mt => ({
      ...mt,
      date: `${mt.year}-01-01`, // Approximate date for membership transactions
      type: 'Membership Fee',
      memberName: members.find(m => m.id === mt.memberId)?.fullName || 'I panjohur',
      collection: 'membershipTransactions'
    }))
  ];

  // Sort all transactions by date desc
  const sortedTransactions = [...allTransactions].sort((a: any, b: any) => {
    if (a.collection === 'membershipTransactions' && b.collection === 'membershipTransactions') {
      return b.year - a.year;
    } else if (a.collection === 'donations' && b.collection === 'donations') {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    } else {
      // Compare year vs date
      const aYear = a.collection === 'membershipTransactions' ? a.year : new Date(a.date).getFullYear();
      const bYear = b.collection === 'membershipTransactions' ? b.year : new Date(b.date).getFullYear();
      return bYear - aYear;
    }
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Input Form */}
      <div className="lg:col-span-1">
        <div className="bg-white p-6 rounded-lg shadow-md border border-emerald-100 sticky top-4">
          <h3 className="text-lg font-bold mb-4 text-emerald-900 flex items-center gap-2">
            <Coins size={20} /> Regjistro Pagesën
          </h3>
          <form onSubmit={handleAddDonation} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zgjidh Anëtarin</label>
              <select 
                required 
                className="w-full p-2 border rounded focus:ring-2 focus:ring-emerald-500"
                value={formData.memberId}
                onChange={e => setFormData({...formData, memberId: e.target.value})}
                aria-label="Zgjidh anëtarin"
              >
                <option value="">-- Zgjidh Anëtarin --</option>
                {members.map(m => (
                  <option key={m.id} value={m.id}>{m.fullName}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shuma</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input 
                  required 
                  type="number" 
                  step="0.01" 
                  className="w-full pl-8 p-2 border rounded focus:ring-2 focus:ring-emerald-500"
                  value={formData.amount}
                  onChange={e => setFormData({...formData, amount: e.target.value})}
                  placeholder="Shkruani shumën"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
              <input 
                required 
                type="date" 
                className="w-full p-2 border rounded"
                value={formData.date}
                onChange={e => setFormData({...formData, date: e.target.value})}
                placeholder="Zgjidhni datën"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lloji</label>
              <select 
                className="w-full p-2 border rounded"
                value={formData.type}
                onChange={e => setFormData({...formData, type: e.target.value})}
                aria-label="Zgjidh llojin"
              >
                <option>Tarifa e Anëtarësisë</option>
                <option>Dhurim</option>
                <option>Fondi i Ndërtimit</option>
                <option>Zakat</option>
              </select>
            </div>

            <button type="submit" className="w-full bg-emerald-600 text-white py-2 rounded-lg font-medium hover:bg-emerald-700 transition shadow-sm">
              Regjistro Transaksionin
            </button>
          </form>
        </div>
      </div>

      {/* History List */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
            <h3 className="font-semibold text-gray-700">Transaksionet e Fundit</h3>
            <span className="text-xs text-gray-500">{sortedTransactions.length} regjistrime</span>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {/* Desktop Table */}
            <div className="hidden md:block">
              <table className="w-full text-left">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase sticky top-0">
                  <tr>
                    <th className="px-4 py-2">Data</th>
                    <th className="px-4 py-2">Anëtari</th>
                    <th className="px-4 py-2">Lloji</th>
                    <th className="px-4 py-2">Shuma</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {sortedTransactions.map(t => (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600">{t.date}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{t.memberName}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">{t.type}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-emerald-600 font-bold">${t.amount.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => handleDelete(t.id, t.collection)} className="text-gray-300 hover:text-red-500" aria-label="Fshi transaksionin">
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile Accordion */}
            <div className="md:hidden space-y-2">
              {sortedTransactions.map(t => {
                const isExpanded = expandedTransactions.has(t.id);
                return (
                  <div key={t.id} className="bg-gray-50 rounded-lg border border-gray-200">
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedTransactions);
                        if (isExpanded) {
                          newExpanded.delete(t.id);
                        } else {
                          newExpanded.add(t.id);
                        }
                        setExpandedTransactions(newExpanded);
                      }}
                      className="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-100"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{t.memberName}</div>
                        <div className="text-sm text-gray-600">{t.date}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-emerald-600 font-bold">${t.amount.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">{isExpanded ? '▼' : '▶'}</div>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-4 py-2 border-t border-gray-200 bg-white">
                        <div className="space-y-1 text-sm">
                          <div><strong>Lloji:</strong> <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">{t.type}</span></div>
                          <div className="flex justify-between">
                            <span></span>
                            <button onClick={() => handleDelete(t.id, t.collection)} className="text-red-500 hover:text-red-700 text-sm">
                              Fshi
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ------------------------------------------------------------------
  MAIN APPLICATION
  ------------------------------------------------------------------
*/
const App = () => {
  const { user } = useAuth();
  const { data: members } = useCollection('members', user);
  const { data: donations } = useCollection('donations', user);
  const { data: membershipTransactions } = useCollection('membershipTransactions', user);
  const { data: admins } = useCollection('admins', user);

  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<ProjectorView members={members} donations={donations} membershipTransactions={membershipTransactions} />} />
        <Route path="/admin" element={
          isAdminAuthenticated ? 
            <AdminPanel members={members} donations={donations} membershipTransactions={membershipTransactions} user={user} /> : 
            <AdminLogin onLogin={() => setIsAdminAuthenticated(true)} admins={admins} />
        } />
      </Routes>
    </Router>
  );
};

export default App;

const refinedStyle = {
  padding: '16px', // Adjusted padding for better balance
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // Soft shadow for subtle depth
  borderRadius: '8px', // Smooth borders
  letterSpacing: '0.5px', // Improved letter spacing
  fontWeight: '500', // Enhanced font weight for hierarchy
};

